import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import userService from '../services/userService';
import inventoryService from '../services/inventoryService';
import wishlistService from '../services/wishlistService';
import tradeService from '../services/tradeService';
import LoadingSpinner from '../components/LoadingSpinner';
import ProfileContactSection from '../components/ProfileContactSection';
import UserAvatar from '../components/UserAvatar';
import InfoNotice from '../components/InfoNotice';
import usePageTitle from '../utils/usePageTitle';
import './UserProfilePage.css';

const UserProfilePage = ({ username }) => {
  const { currentUser } = useContext(AuthContext);
  const { showToast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({
    collection: {
      products: 0,
      insertSets: 0,
      cards: 0
    },
    wishlist: {
      products: 0,
      insertSets: 0,
      cards: 0
    },
    availableCards: {
      products: 0,
      insertSets: 0,
      cards: 0
    }
  });
  
  // Set the page title based on profile data
  usePageTitle(profile ? `${profile.name}'s Profile` : 'User Profile');

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch user data by username
        const user = await userService.getUserByUsername(username);
        setProfile(user);
      } catch (err) {
        console.error('Error fetching user profile:', err);
        // Handle error format
        if (err.response) {
          // Server responded with error
          setError(err.response.data?.message || 'Failed to load profile');
        } else if (err.request) {
          // Request made but no response
          setError('No response from server. Please try again later.');
        } else {
          // Something else went wrong
          setError(err.message || 'Failed to load profile');
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (username) {
      fetchProfile();
    }
  }, [username]);
  
  // Fetch user stats once we have the profile
  useEffect(() => {
    const fetchStats = async () => {
      if (!profile || !profile.id) return;
      
      try {
        // Determine if we should fetch collection stats
        // Only fetch if the collection is not private or the current user is viewing their own profile
        const shouldFetchInventoryStats = !profile.isCollectionPrivate || 
          (currentUser && currentUser.id === profile.id);
        
        // Fetch wishlist and trade stats always
        const [wishlistStats, tradeStats] = await Promise.all([
          wishlistService.getUserWishlistStats(profile.id),
          tradeService.getUserCardsForTradeStats(profile.id)
        ]);
        
        // Fetch inventory stats conditionally
        let inventoryStats = { totalProducts: 0, totalInsertSets: 0, totalCards: 0 };
        if (shouldFetchInventoryStats) {
          inventoryStats = await inventoryService.getUserInventoryStats(profile.id);
        }
        
        // Set all the stats at once
        setStats({
          collection: {
            products: inventoryStats.totalProducts || 0,
            insertSets: inventoryStats.totalInsertSets || 0,
            cards: inventoryStats.totalCards || 0
          },
          wishlist: {
            products: wishlistStats.totalProducts || 0,
            insertSets: wishlistStats.totalInsertSets || 0,
            cards: wishlistStats.totalCards || 0
          },
          availableCards: {
            products: tradeStats.totalProducts || 0,
            insertSets: tradeStats.totalInsertSets || 0,
            cards: tradeStats.totalCards || 0
          }
        });
      } catch (statsError) {
        console.error('Error fetching user stats:', statsError);
        // Don't set an error, just log it, as we still want to show the profile
      }
    };
    
    fetchStats();
  }, [profile, currentUser]);
  
  if (loading) {
    return <div className="public-profile__loading"><LoadingSpinner /></div>;
  }
  
  if (error) {
    return (
      <div className="public-profile">
        <div className="public-profile__error">
          <div className="public-profile__error-message">{error}</div>
          <p>This profile was not found.</p>
          <Link to="/" className="public-profile__back-link">Go back to homepage</Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="public-profile">
      <div className="public-profile__header">
        <div className="public-profile__identity">
          <UserAvatar 
            user={profile}
            size="xlarge"
            className="public-profile__picture"
          />
          
          <div className="public-profile__info-container">
            <h1 className="public-profile__username">{profile.name}</h1>
            <p className="public-profile__member-since">
              {profile.name !== profile.username && (
                <><span>@{profile.username}</span> &middot; </>
              )}
              Member since {new Date(profile.memberSince).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>
      
      <div className="public-profile__content">
        {currentUser && currentUser.username === username && (
          <>
            <InfoNotice 
              icon="share-alt" 
              variant="info"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                showToast('URL copied to clipboard!', 'success');
              }}
            >
              This is your public profile page. Share this URL with others to let them view your collection, wishlist, and cards for trade. Click here to copy this URL.
            </InfoNotice>
            
            <InfoNotice icon="database" variant="success">
              {[
                'You can add to your collection, wishlist, and cards for trade by browsing our database of ',
                <Link to="/trading-card-sets">Trading Card Sets</Link>,
                '.'
              ]}
            </InfoNotice>
          </>
        )}
        <div className="public-profile__sections">
          {/* Collection Section - Only shown if collection is not private or viewing own profile */}
          {(!profile.isCollectionPrivate || profile.id === currentUser?.id) && (
            <div className="public-profile__section">
              <h2 className="public-profile__section-header">Cards in Collection</h2>
              <div className="public-profile__count">{stats.collection.cards.toLocaleString()} Cards</div>
              <Link 
                to={`/@${username}/collection`} 
                className="public-profile__view-button"
              >
                View Collection
              </Link>
            </div>
          )}
          
          {/* Wishlist Section */}
          <div className="public-profile__section">
            <h2 className="public-profile__section-header">Cards on Wishlist</h2>
            <div className="public-profile__count">{stats.wishlist.cards.toLocaleString()} Cards</div>
            <Link 
              to={`/@${username}/wishlist`} 
              className="public-profile__view-button"
            >
              View Wishlist
            </Link>
          </div>
          
          {/* Available Cards Section */}
          <div className="public-profile__section">
            <h2 className="public-profile__section-header">Available Cards</h2>
            <div className="public-profile__count">{stats.availableCards.cards.toLocaleString()} Cards</div>
            <Link 
              to={`/@${username}/available-cards`} 
              className="public-profile__view-button"
            >
              View Available Cards
            </Link>
          </div>

          {/* Contact Section - Using the ProfileContactSection component */}
          <ProfileContactSection user={profile} />
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;